import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map as IMap } from 'immutable';
import { Container } from 'cstar-react-primitives/lib/layout/Container';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { TertiaryButton } from 'cstar-react-primitives/lib/redux-form/buttons/TertiaryButton';
import { PrimaryButton } from 'cstar-react-primitives/lib/redux-form/buttons/PrimaryButton';
import { useDispatch, useStore } from 'react-redux';
import { change, getFormValues } from 'redux-form/immutable';
import {
    NEW_ITEM_FORM_NAME,
    DIGITAL_GREETINGS_FIELD
} from '../item/newItemForm';
import messageIntl from './messageIntl';
import { formatSelectedGreetingOrder } from './digitalGreetingsUtils';
import { logError } from '../utils/errorUtils';
import { itemSegmentData } from '../segment/segmentSelectors';

const MODAL_ACTIONS = {
    REMOVE: 'remove',
    RESELECT: 'reselect'
};

const DigitalGreetingsModal = ({
    intl,
    modalActionRef,
    setIsModalOpen,
    setIsGreetingSelected,
    formName
}) => {
    const dispatch = useDispatch();
    const store = useStore();

    const handleModalClose = useCallback(() => {
        try {
            const buttonClickSegmentData = {
                title: intl.formatMessage(messageIntl.digitalGreetingsCancelButtonText),
                referrer: '',
                url: window.location.href || '',
                destination: window.location.href || '',
                context: 'MODAL'
            };
            dispatch({
                type: 'GREETING_BUTTON_CLICK',
                meta: {
                    segment: {
                        eventAction: 'Button Click',
                        eventData: {
                            ...buttonClickSegmentData
                        }
                    }
                }
            });
            setIsModalOpen(false);
        } catch (error) {
            logError('Failed to close modal:', error);
            setIsModalOpen(false);
        }
    }, [setIsModalOpen, dispatch]);

    const handleGreetingRemoval = useCallback(() => {
        try {
            const currentFormValues = getFormValues(formName)(store.getState());
            const segmentData = itemSegmentData(currentFormValues);
            dispatch({
                type: 'DIGITAL_GREETING_REMOVED',
                meta: {
                    segment: {
                        eventAction: 'Digital Greeting Removed',
                        eventData: {
                            ...segmentData
                        }
                    }
                }
            });
            dispatch(change(formName, DIGITAL_GREETINGS_FIELD, null));
            setIsModalOpen(false);
            setIsGreetingSelected(false);
        } catch (error) {
            logError('Failed to remove greeting:', error);
            setIsModalOpen(false);
        }
    }, [dispatch, formName, setIsModalOpen, setIsGreetingSelected, setIsModalOpen]);

    const handleGreetingsOrderCompleted = useCallback((order) => {
        try {
            const formattedOrder = formatSelectedGreetingOrder(order);
            dispatch(change(formName, DIGITAL_GREETINGS_FIELD, IMap(formattedOrder)));
            const currentFormValues = getFormValues(formName)(store.getState());
            const segmentData = itemSegmentData(currentFormValues);
            dispatch({
                type: 'DIGITAL_GREETING_ADDED',
                meta: {
                    segment: {
                        eventAction: 'Digital Greeting Added',
                        eventData: {
                            ...segmentData
                        }
                    }
                }
            });
            setIsModalOpen(false);
        } catch (error) {
            logError('Error processing greeting order', error);
            setIsModalOpen(false);
        }
    }, [dispatch, setIsModalOpen, formName]);

    const modalActionConfig = useMemo(() => ({
        remove: {
            btnText: intl.formatMessage(messageIntl.digitalGreetingsRemoveButtonText),
            cashbotName: 'digital-greetings-modal-remove',
            helperTextKey: 'digitalGreetingsRemoveHelper',
            onClick: handleGreetingRemoval
        },
        reselect: {
            btnText: intl.formatMessage(messageIntl.digitalGreetingsReselectButtonText),
            cashbotName: 'digital-greetings-modal-reselect',
            helperTextKey: 'digitalGreetingsReselectHelper',
            onClick: () => {}
        }
    }), [intl, handleGreetingRemoval, handleModalClose]);

    useEffect(() => {
        if (modalActionRef !== 'reselect' || !window.addACardClient) {
            return () => {};
        }

        const containerId = 'digital-greetings-modal-action-btn';
        const button = document.getElementById(containerId);

        const wrappedOrderCompletedHandler = (order) => {
            try {
                handleGreetingsOrderCompleted(order);
            } catch (error) {
                logError('Error in order completion handler', error);
            } finally {
                window.addACardClient.off('order-completed', wrappedOrderCompletedHandler);
            }
        };

        const handleButtonClick = (event) => {
            event.preventDefault();
            try {
                window.addACardClient.addCard();
                window.addACardClient.on('order-completed', wrappedOrderCompletedHandler);
            } catch (err) {
                logError('Failed to open AddACard interface:', err);
            }
        };

        const handleClose = () => {
            window.addACardClient.off('order-completed', wrappedOrderCompletedHandler);
            window.addACardClient.off('close', handleClose);
            setIsModalOpen(false);
        };

        (async () => {
            try {
                await window.addACardClient.load();
                if (button) {
                    button.addEventListener('click', handleButtonClick);
                    window.addACardClient.on('close', handleClose);
                }
            } catch (err) {
                logError('Failed to load AddACard client:', err);
            }
        })();

        return () => {
            if (button) {
                button.removeEventListener('click', handleButtonClick);
            }
            if (window.addACardClient) {
                window.addACardClient.off('order-completed', handleGreetingsOrderCompleted);
                window.addACardClient.off('close', handleClose);
            }
        };
    }, [modalActionRef, handleGreetingsOrderCompleted]);

    const selectedAction = modalActionConfig[modalActionRef];

    return (
        <div className="digital-greetings-modal" role="dialog" aria-modal="true">
            <Container customClassName="digital-greetings-modal-overlay">
                <div className="digital-greetings-modal__content">
                    <FormattedMessage {...messageIntl[selectedAction.helperTextKey]} />
                </div>
                <div className="digital-greetings-modal__tertiary-btn">
                    <TertiaryButton
                        btnText={intl.formatMessage(messageIntl.digitalGreetingsCancelButtonText)}
                        cashbotName="digital-greetings-modal-cancel"
                        onClick={handleModalClose}
                    />
                </div>
                <div
                    className="digital-greetings-modal__action-btn"
                    id="digital-greetings-modal-action-btn"
                >
                    <PrimaryButton
                        btnText={selectedAction.btnText}
                        cashbotName={selectedAction.cashbotName}
                        onClick={selectedAction.onClick}
                    />
                </div>
            </Container>
        </div>
    );
};

DigitalGreetingsModal.propTypes = {
    modalActionRef: PropTypes.oneOf(Object.values(MODAL_ACTIONS)).isRequired,
    setIsModalOpen: PropTypes.func.isRequired,
    setIsGreetingSelected: PropTypes.func,
    intl: intlShape.isRequired,
    formName: PropTypes.string
};

DigitalGreetingsModal.defaultProps = {
    setIsGreetingSelected: () => {},
    formName: NEW_ITEM_FORM_NAME
};

export default injectIntl(DigitalGreetingsModal);
